import { useEffect, useRef, useState } from "react";
import { MoreHorizontal, Pencil, Copy } from "lucide-react";
import type { Trace } from "../shared/schema";
import type { WorkspaceModel } from "./useWorkspace";
import { paperOpacity } from "../shared/trace-paper";
import { OpacityControl, TraceControls } from "./TraceControls";
import { SourceControls } from "./SourceControls";
import { liftTracingSheet } from "./tracing-sheet";

export function TraceToolbar({
  model,
  trace,
}: {
  model: WorkspaceModel;
  trace: Trace;
}) {
  const [panel, setPanel] = useState<"opacity" | "more" | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const drawing = model.annotation.trace?.id === trace.id;
  useEffect(() => setPanel(null), [trace.id]);
  useEffect(() => {
    if (!panel) return;
    const outside = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setPanel(null);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [panel]);
  return (
    <div
      ref={root}
      className="trace-toolbar floating-surface"
      aria-label="Tracing sheet tools"
      onKeyDown={(e) => {
        if (e.key === "Escape" && panel) {
          e.preventDefault();
          e.stopPropagation();
          setPanel(null);
        } else if ((e.target as Element).matches("input")) e.stopPropagation();
      }}
    >
      <div
        className="trace-toolbar-row"
        role="toolbar"
        aria-label={
          drawing ? `Drawing on ${trace.props.name}` : trace.props.name
        }
      >
        <button
          className="trace-toolbar-name"
          title="Rename tracing sheet"
          onClick={() => {
            if (drawing) model.annotation.end();
            model.editor.select(trace.id);
            setPanel(panel === "more" ? null : "more");
          }}
        >
          {drawing ? "Drawing on: " : ""}
          {trace.props.name}
        </button>
        <button
          className={drawing ? "primary" : ""}
          onClick={() => {
            setPanel(null);
            if (drawing) {
              model.annotation.end();
              model.editor.select(trace.id);
            } else {
              liftTracingSheet(model.editor, trace);
              model.annotation.begin(trace.id);
            }
          }}
        >
          {drawing ? (
            "Done"
          ) : (
            <>
              <Pencil size={16} /> Draw
            </>
          )}
        </button>
        <button
          onClick={() => {
            model.annotation.end();
            liftTracingSheet(model.editor, trace);
            model.setPickingTrace(trace.id);
            setPanel(null);
          }}
        >
          <Copy size={16} /> Copy from below
        </button>
        <button
          aria-expanded={panel === "opacity"}
          onClick={() => setPanel(panel === "opacity" ? null : "opacity")}
        >
          Paper {Math.round(paperOpacity(trace) * 100)}%
        </button>
        <button
          aria-label="More tracing sheet actions"
          title="More"
          aria-expanded={panel === "more"}
          onClick={() => {
            if (drawing) model.annotation.end();
            model.editor.select(trace.id);
            setPanel(panel === "more" ? null : "more");
          }}
        >
          <MoreHorizontal size={19} />
        </button>
      </div>
      {panel === "opacity" && (
        <div className="trace-toolbar-panel floating-surface">
          <OpacityControl model={model} shape={trace} label="Paper opacity" />
          <small>Changes the paper, not your artwork.</small>
        </div>
      )}
      {panel === "more" && (
        <div className="trace-toolbar-panel floating-surface composition-controls">
          <TraceControls model={model} />
          <SourceControls model={model} source={trace} />
          <button
            onClick={() => {
              model.editor.toggleLock([trace.id]);
              model.editor.selectNone();
            }}
          >
            Lock sheet
          </button>
          <button
            onClick={() => {
              model.editor.deleteShapes([trace.id]);
            }}
          >
            Delete sheet
          </button>
        </div>
      )}
    </div>
  );
}
